import React from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/auth/LoginPage';
import ListeSinistres from './components/sinistres/ListeSinistres';
import FicheSinistre from './components/sinistres/FicheSinistre';
import FicheDossier from './components/sinistres/FicheDossier';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import Sidebar from './components/layout/Sidebar';
import ListeIntermediaires from './components/intermediaires/ListeIntermediaires';
import FicheIntermediaire from './components/intermediaires/FicheIntermediaire';
import ListePrestataires from './components/prestataires/ListePrestataires';
import FichePrestataire from './components/prestataires/FichePrestataire';
import ListeClients from './components/clients/ListeClients';
import ClientDetail from './components/clients/ClientDetail';
import FicheClient from './components/clients/FicheClient';
import PortefeuilleContrats from './components/polices/PortefeuilleContrats';
import PortefeuilleGF from './components/polices/PortefeuilleGF';
import FichePolice from './components/polices/FichePolice';
import FicheAmendement from './components/polices/FicheAmendement';
import FicheGF from './components/polices/FicheGF';
import DemandesOuverture from './components/polices/DemandesOuverture';
import DemandesEtude from './components/polices/DemandesEtude';
import DeclarationsCA from './components/polices/DeclarationsCA';
import Dashboard from './components/dashboard/Dashboard';
import UtilisateursList from './components/utilisateurs/UtilisateursList';
import FicheUtilisateur from './components/utilisateurs/FicheUtilisateur';
import GestionDocumentaire from './components/documents/GestionDocumentaire';
import MonCompte from './components/auth/MonCompte';
import ChatPanel from './components/chat/ChatPanel';
import NotesInternesPanel from './components/notes/NotesInternesPanel';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';

// Lazy load Phase B components — if a file is missing, only the specific route fails (not whole app)
const PendingValidation = React.lazy(() => import('./components/sinistres/PendingValidation'));
const PendingPayment    = React.lazy(() => import('./components/sinistres/PendingPayment'));
const PendingBalances   = React.lazy(() => import('./components/sinistres/PendingBalances'));
const ClosedDossiers    = React.lazy(() => import('./components/sinistres/ClosedDossiers'));

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter/>
      </AuthProvider>
    </BrowserRouter>
  );
}

function NavigateByRole() {
  const { isVoyageur, dossierId, procedureId, isClient, clientId, isArcus } = useAuth();
  if (isVoyageur && dossierId && procedureId) {
    return <Navigate to={`/sinistres/${procedureId}/dossiers/${dossierId}`} replace/>;
  }
  if (isClient && clientId) {
    return <Navigate to={`/clients/${clientId}/detail`} replace/>;
  }
  // Le tableau de bord ne concerne que les profils internes (Arcus).
  // Les autres (courtier, client, voyageur sans dossier) vont vers les sinistres ou clients.
  if (!isArcus) {
    return <Navigate to="/clients" replace/>;
  }
  return <Navigate to="/dashboard" replace/>;
}

// Le Dashboard est réservé aux profils internes (Arcus). Sinon, redirection.
function DashboardGuard({ children }) {
  const { isArcus, isClient, clientId, isVoyageur, dossierId, procedureId } = useAuth();
  if (isArcus) return children;
  if (isVoyageur && dossierId && procedureId) return <Navigate to={`/sinistres/${procedureId}/dossiers/${dossierId}`} replace/>;
  if (isClient && clientId) return <Navigate to={`/clients/${clientId}/detail`} replace/>;
  return <Navigate to="/clients" replace/>;
}


function ClientGuard() {
  const { isClient, clientId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isClient || !clientId) return;
    const path = location.pathname;
    // Pages autorisées : fiche client + detail + polices + amendements + GF
    const isAllowed =
      path.startsWith(`/clients/${clientId}`) ||
      path.startsWith('/polices/') ||
      path.startsWith('/amendements/') ||
      path.startsWith('/gf/') ||
      path.startsWith('/garanties/') ||
      path === '/';
    if (!isAllowed) {
      navigate(`/clients/${clientId}/detail`, { replace: true });
    }
  });
  return null;
}

function VoyageurGuard() {
  const { isVoyageur, dossierId, procedureId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isVoyageur || !dossierId || !procedureId) return;
    const allowed = `/sinistres/${procedureId}/dossiers/${dossierId}`;
    // Redirect to dossier if on any other page
    if (!location.pathname.startsWith(allowed)) {
      navigate(allowed, { replace: true });
    }
  });  // runs on every render/navigation

  return null;
}

function AppRouter() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:'#1A1A1A' }}>
      <div style={{ color:'#E8671A', fontSize:18, fontWeight:700 }}>ARCUS<span style={{ color:'#fff' }}>CONNECT</span></div>
    </div>
  );

  if (!isAuthenticated) return (
    <Routes>
      <Route path="/forgot-password"        element={<ForgotPassword/>}/>
      <Route path="/reset-password/:token"  element={<ResetPassword/>}/>
      <Route path="*" element={<LoginPage/>}/>
    </Routes>
  );

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#f8fafc' }}>
      <Sidebar />
      <main style={{ flex:1, minWidth:0, padding:'24px', overflowY:'auto' }}>
        <Routes>
            <Route path="/" element={<NavigateByRole/>} />
            <Route path="/dashboard" element={<DashboardGuard><Dashboard /></DashboardGuard>} />

            {/* Intermédiaires */}
            <Route path="/intermediaires"         element={<ListeIntermediaires />} />
            <Route path="/intermediaires/nouveau" element={<FicheIntermediaire />} />
            <Route path="/intermediaires/:id"     element={<FicheIntermediaire />} />

            {/* Prestataires & TOs (Phase A) */}
            <Route path="/prestataires"          element={<ListePrestataires />} />
            <Route path="/prestataires/nouveau"  element={<FichePrestataire />} />
            <Route path="/prestataires/:id"      element={<FichePrestataire />} />

            {/* Sous-catégories Claims Management (Phase B) — lazy loaded */}
            <Route path="/dossiers/pending-validation" element={<React.Suspense fallback={<div style={{padding:24,color:'#94A3B8'}}>Loading...</div>}><PendingValidation /></React.Suspense>} />
            <Route path="/dossiers/pending-payment"    element={<React.Suspense fallback={<div style={{padding:24,color:'#94A3B8'}}>Loading...</div>}><PendingPayment /></React.Suspense>} />
            <Route path="/dossiers/pending-balances"   element={<React.Suspense fallback={<div style={{padding:24,color:'#94A3B8'}}>Loading...</div>}><PendingBalances /></React.Suspense>} />
            <Route path="/dossiers/closed"             element={<React.Suspense fallback={<div style={{padding:24,color:'#94A3B8'}}>Loading...</div>}><ClosedDossiers /></React.Suspense>} />

            {/* Clients */}
            <Route path="/clients"         element={<ListeClients />} />
            <Route path="/clients/nouveau" element={<FicheClient />} />
            <Route path="/clients/:id"     element={<FicheClient />} />
            <Route path="/clients/:id/detail" element={<ClientDetail />} />

            {/* RC Pro */}
            <Route path="/polices"     element={<PortefeuilleContrats produit="rc_pro" />} />
            <Route path="/polices/:id" element={<FichePolice />} />

            {/* Avenants RC Pro */}
            <Route path="/amendements/:id"              element={<FicheAmendement />} />
            <Route path="/amendements/nouveau/:policeId" element={<CreateAmendement />} />

            {/* Garantie Financière */}
            <Route path="/gf"     element={<PortefeuilleGF />} />
            <Route path="/gf/:id" element={<FicheGF />} />
            <Route path="/demandes-ouverture" element={<DemandesOuverture />} />
            <Route path="/demandes-etude" element={<DemandesEtude />} />
            <Route path="/declarations-ca" element={<DeclarationsCA />} />
            <Route path="/demandes-etude-rcpro" element={<DemandesEtude produit="rc_pro" />} />

            {/* ── Gestion Sinistre ── */}
            <Route path="/sinistres"     element={<ListeSinistres />} />
            <Route path="/sinistres/:id" element={<FicheSinistre />} />
            <Route path="/sinistres/:procedureId/dossiers/:dossierId" element={<FicheDossier />} />

            {/* ── Administration (IT) ── */}
            <Route path="/utilisateurs"          element={<UtilisateursList />} />
            <Route path="/utilisateurs/nouveau"  element={<FicheUtilisateur />} />
            <Route path="/utilisateurs/:id"      element={<FicheUtilisateur />} />
            <Route path="/gestion-documentaire"  element={<GestionDocumentaire />} />

            {/* ── Espace compte personnel (tous les rôles) ── */}
            <Route path="/mon-compte" element={<MonCompte />} />
          </Routes>
          <VoyageurGuard/>
          <ClientGuard/>
        </main>
        <ChatPanel />
        <NotesInternesPanel />
      </div>
  );
}

// Crée un amendement via l'API puis redirige
function CreateAmendement() {
  const { policeId } = require('react-router-dom').useParams();
  const navigate     = require('react-router-dom').useNavigate();
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    const token = localStorage.getItem('arcus_token');
    fetch(`/api/polices/${policeId}/amendements`, {
      method:'POST',
      headers: {
        'Content-Type':'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then(r => r.json())
      .then(data => {
        if (data.id) navigate(`/amendements/${data.id}`, { replace:true });
        else setError(data.error || 'Erreur création amendement');
      })
      .catch(e => setError(e.message));
  }, [policeId, navigate]);

  if (error) return <div style={{padding:24,color:'#DC2626'}}>Erreur : {error}</div>;
  return <div style={{padding:24,color:'#64748B'}}>Création de l'amendement…</div>;
}
